import { useState, useEffect } from 'react';
import { ArrowLeft, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import {
  useGenerateImagePrompt,
  type AspectRatio,
} from '../features/article/api/imageQueries';
import {
  getImageGenerationMode,
  setImageGenerationMode,
  type ImageGenerationMode,
} from '../utils/headerImageStorage';

/**
 * Props for the ImageGenerationWizard component
 */
export interface ImageGenerationWizardProps {
  articleId: string;
  articleTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Force advanced mode (used for regeneration to allow prompt editing) */
  forceAdvancedMode?: boolean;
  /** Callback when Easy mode generation starts (dialog will close, parent handles generation) */
  onEasyModeStart?: (config: { style: string; aspectRatio: AspectRatio }) => void;
  /** Callback when Advanced mode generation starts (dialog will close, parent handles generation) */
  onAdvancedModeStart?: (config: { prompt: string; style: string; aspectRatio: AspectRatio }) => void;
}

/** Wizard step type */
type WizardStep = 'config' | 'prompt';

/**
 * ImageGenerationWizard Component
 *
 * A dialog for configuring article header image generation.
 * Easy mode: config → close (parent handles generation)
 * Advanced mode: config → prompt → close (parent handles generation)
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 7.1, 7.2, 7.3, 7.4
 */
export function ImageGenerationWizard({
  articleId,
  articleTitle: _articleTitle, // Kept for API consistency, not used in wizard
  open,
  onOpenChange,
  forceAdvancedMode = false,
  onEasyModeStart,
  onAdvancedModeStart,
}: Readonly<ImageGenerationWizardProps>) {
  // Wizard state
  const [step, setStep] = useState<WizardStep>('config');

  // Mode state (easy or advanced)
  const [mode, setMode] = useState<ImageGenerationMode>(() =>
    forceAdvancedMode ? 'advanced' : getImageGenerationMode()
  );
  
  // Configuration state (Step 1)
  const [selectedStyle, setSelectedStyle] = useState<string>('professional');
  const [customStyle, setCustomStyle] = useState<string>('');
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<AspectRatio>('16:9');

  // Prompt state (Step 2)
  const [editedPrompt, setEditedPrompt] = useState<string>('');

  // Mutations
  const generateImagePromptMutation = useGenerateImagePrompt();

  /**
   * Reset mode when dialog opens based on forceAdvancedMode prop
   */
  useEffect(() => {
    if (open) {
      setMode(forceAdvancedMode ? 'advanced' : getImageGenerationMode());
    }
  }, [open, forceAdvancedMode]);

  /**
   * Reset wizard state when dialog opens/closes
   */
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset state when closing
      setStep('config');
      setEditedPrompt('');
      setCustomStyle('');
    }
    onOpenChange(newOpen);
  };

  /**
   * Handle mode toggle between easy and advanced
   */
  const handleModeToggle = (checked: boolean) => {
    const newMode = checked ? 'advanced' : 'easy';
    setMode(newMode);
    setImageGenerationMode(newMode);
  };

  /**
   * Handle Easy mode generation - closes dialog and triggers parent to generate
   */
  const handleEasyModeGenerate = () => {
    if (!articleId) return;
    const styleToUse = customStyle.trim() || selectedStyle;
    onEasyModeStart?.({ style: styleToUse, aspectRatio: selectedAspectRatio });
    onOpenChange(false);
  };

  /**
   * Handle prompt generation (Step 1 → Step 2)
   * Requirements: 2.3
   */
  const handleGeneratePrompt = () => {
    if (!articleId) return;

    const styleToUse = customStyle.trim() || selectedStyle;

    generateImagePromptMutation.mutate(
      { articleId, style: styleToUse },
      {
        onSuccess: (data) => {
          setEditedPrompt(data.prompt);
          setStep('prompt');
        },
        onError: (error) => {
          console.error('Error generating prompt:', error);
          toast.error('Failed to generate prompt: ' + error.message);
        },
      }
    );
  };

  /**
   * Handle Advanced mode generation - closes dialog and triggers parent to generate
   */
  const handleAdvancedModeGenerate = () => {
    if (!articleId || !editedPrompt.trim()) return;

    const styleToUse = customStyle.trim() || selectedStyle;
    onAdvancedModeStart?.({
      prompt: editedPrompt,
      style: styleToUse,
      aspectRatio: selectedAspectRatio,
    });
    onOpenChange(false);
  };

  /**
   * Navigate back to config step
   */
  const handleBackToConfig = () => {
    setStep('config');
  };

  /**
   * Get dialog title based on current step
   */
  const getDialogTitle = () => {
    switch (step) {
      case 'config':
        return 'Configure Image Generation';
      case 'prompt':
        return 'Review & Edit Prompt';
    }
  };

  /**
   * Get dialog description based on current step
   */
  const getDialogDescription = () => {
    switch (step) {
      case 'config':
        return 'Select your preferred style and aspect ratio for the article header image.';
      case 'prompt':
        return 'Review the generated prompt and make any edits before generating the image.';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        {/* Step 1: Configuration */}
        {step === 'config' && (
          <div className="space-y-4">
            {/* Mode Toggle - only show if not forced to advanced */}
            {!forceAdvancedMode && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="generation-mode" className="text-sm font-medium">
                    {mode === 'easy' ? 'Easy Mode' : 'Advanced Mode'}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {mode === 'easy'
                      ? 'One-click generation with automatic prompt'
                      : 'Review and edit prompt before generating'}
                  </p>
                </div>
                <Switch
                  id="generation-mode"
                  checked={mode === 'advanced'}
                  onCheckedChange={handleModeToggle}
                />
              </div>
            )}

            <div>
              <Label htmlFor="image-style" className="text-sm font-medium mb-2 block">
                Select Style
              </Label>
              <select
                id="image-style"
                value={selectedStyle}
                onChange={(e) => setSelectedStyle(e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <optgroup label="Current Styles">
                  <option value="professional">Professional</option>
                  <option value="modern">Modern</option>
                  <option value="illustration">Illustration</option>
                  <option value="photorealistic">Photorealistic</option>
                </optgroup>
                <optgroup label="Business">
                  <option value="minimalist">Minimalist</option>
                  <option value="corporate">Corporate</option>
                  <option value="tech">Tech/Startup</option>
                </optgroup>
                <optgroup label="Creative">
                  <option value="abstract">Abstract</option>
                  <option value="artistic">Artistic</option>
                  <option value="cinematic">Cinematic</option>
                </optgroup>
                <optgroup label="Specialized">
                  <option value="infographic">Infographic</option>
                  <option value="editorial">Editorial</option>
                  <option value="vintage">Vintage/Retro</option>
                </optgroup>
                <optgroup label="Premium">
                  <option value="economist">The Economist</option>
                  <option value="financial-times">Financial Times</option>
                  <option value="bloomberg">Bloomberg</option>
                  <option value="monocle">Monocle</option>
                  <option value="new-yorker">New Yorker</option>
                  <option value="ligne-claire">Ligne Claire</option>
                </optgroup>
              </select>
            </div>
            <div>
              <Label htmlFor="custom-style" className="text-sm font-medium mb-2 block">
                Or Custom Style (optional)
              </Label>
              <Input
                id="custom-style"
                value={customStyle}
                onChange={(e) => setCustomStyle(e.target.value)}
                placeholder="e.g., watercolor painting, minimalist, abstract art"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Describe your preferred style to override the selection above
              </p>
            </div>
            <div>
              <Label htmlFor="aspect-ratio" className="text-sm font-medium mb-2 block">
                Aspect Ratio
              </Label>
              <select
                id="aspect-ratio"
                value={selectedAspectRatio}
                onChange={(e) => setSelectedAspectRatio(e.target.value as AspectRatio)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="1:1">1:1 (Square)</option>
                <option value="2:3">2:3 (Portrait)</option>
                <option value="3:2">3:2 (Photography)</option>
                <option value="3:4">3:4 (Portrait)</option>
                <option value="4:3">4:3 (Standard)</option>
                <option value="4:5">4:5 (Portrait)</option>
                <option value="5:4">5:4 (Near Square)</option>
                <option value="9:16">9:16 (Vertical)</option>
                <option value="16:9">16:9 (Landscape)</option>
                <option value="21:9">21:9 (Ultrawide)</option>
              </select>
            </div>
          </div>
        )}


        {/* Step 2: Prompt Review/Edit */}
        {step === 'prompt' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="image-prompt" className="text-sm font-medium mb-2 block">
                Image Generation Prompt
              </Label>
              <textarea
                id="image-prompt"
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                className="w-full min-h-[200px] px-3 py-2 border rounded-md font-mono text-sm"
                placeholder="Generated prompt will appear here..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Edit the prompt to refine the image generation if needed
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {/* Step 1: Config */}
          {step === 'config' && (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              {mode === 'easy' ? (
                <Button onClick={handleEasyModeGenerate}>
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Generate Image
                </Button>
              ) : (
                <Button
                  onClick={handleGeneratePrompt}
                  disabled={generateImagePromptMutation.isPending}
                >
                  {generateImagePromptMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating Prompt...
                    </>
                  ) : (
                    'Generate Prompt'
                  )}
                </Button>
              )}
            </>
          )}

          {/* Step 2: Prompt */}
          {step === 'prompt' && (
            <>
              <Button variant="outline" onClick={handleBackToConfig}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleAdvancedModeGenerate}
                disabled={!editedPrompt.trim()}
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Generate Image
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ImageGenerationWizard;
